import { useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

type PortfolioProject = {
  img: string;
  name: string;
};

type PortfolioGalleryProps = {
  projects: PortfolioProject[];
  activeIndex: number | null;
  onActiveIndexChange: (index: number | null) => void;
};

export function PortfolioGallery({ projects, activeIndex, onActiveIndexChange }: PortfolioGalleryProps) {
  const activeProject = activeIndex === null ? null : projects[activeIndex];

  const closeOnBackdropClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) onActiveIndexChange(null);
  };

  const showPrevious = () => {
    if (activeIndex === null || projects.length === 0) return;
    onActiveIndexChange((activeIndex - 1 + projects.length) % projects.length);
  };

  const showNext = () => {
    if (activeIndex === null || projects.length === 0) return;
    onActiveIndexChange((activeIndex + 1) % projects.length);
  };

  useEffect(() => {
    if (activeIndex === null) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") showPrevious();
      if (event.key === "ArrowRight") showNext();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, projects.length]);

  return (
    <Dialog open={activeIndex !== null} onOpenChange={(open) => !open && onActiveIndexChange(null)}>
      <DialogContent
        aria-describedby={undefined}
        className="portfolio-gallery-dialog h-dvh w-screen max-w-none translate-x-[-50%] translate-y-[-50%] gap-0 overflow-hidden rounded-none border-0 bg-foreground/95 p-0 shadow-none"
        onClick={closeOnBackdropClick}
      >
        <VisuallyHidden>
          <DialogTitle>{activeProject?.name ?? "Portfolio"} website design preview</DialogTitle>
        </VisuallyHidden>

        <div className="relative flex min-h-0 flex-1 items-center justify-center px-12 pb-28 pt-14 sm:px-20 sm:pb-32" onClick={closeOnBackdropClick}>
          {activeProject && (
            <div className="flex h-full w-full items-center justify-center overflow-auto" onClick={closeOnBackdropClick}>
              <img
                key={activeProject.img}
                src={activeProject.img}
                alt={`${activeProject.name} website design full preview`}
                className="block max-h-full max-w-full object-contain shadow-2xl animate-fade-in"
              />
            </div>
          )}

          <Button
            type="button"
            size="icon"
            variant="secondary"
            aria-label="Previous website"
            onClick={showPrevious}
            className="absolute left-2 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full shadow-lg sm:left-5"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="secondary"
            aria-label="Next website"
            onClick={showNext}
            className="absolute right-2 top-1/2 h-10 w-10 -translate-y-1/2 rounded-full shadow-lg sm:right-5"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>

          <DialogClose asChild>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              aria-label="Close portfolio gallery"
              className="absolute right-3 top-3 h-10 w-10 rounded-full shadow-lg sm:right-5 sm:top-5"
            >
              <X className="h-5 w-5" />
            </Button>
          </DialogClose>
        </div>

        <div className="absolute inset-x-0 bottom-0 border-t border-background/20 bg-foreground/95 px-3 py-3 sm:px-6">
          <div className="mx-auto flex max-w-5xl gap-2 overflow-x-auto pb-1">
            {projects.map((project, index) => (
              <Button
                key={project.name}
                type="button"
                variant="ghost"
                aria-label={`View ${project.name}`}
                aria-pressed={index === activeIndex}
                onClick={() => onActiveIndexChange(index)}
                className={`h-16 w-20 shrink-0 overflow-hidden rounded-md border-2 p-0 sm:h-20 sm:w-28 ${index === activeIndex ? "border-primary opacity-100" : "border-transparent opacity-60 hover:opacity-100"}`}
              >
                <img src={project.img} alt="" className="h-full w-full object-cover object-top" />
              </Button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}